import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { toggleLoginScreen } from "../actions/AccountAction";
import { getEnvEndpoints } from "../utils/functions";
import "../assets/css/BosuitbreidingPage.css";

// Package/amount configurations
const COMPANY_PACKAGES = {
  bronze: { name: "Bronze", price: "100,00", minAmount: 100, color: "#8b5a2b", bg: "#f8f5f2", border: "#eadfce", description: "Naamvermelding als sponsor" },
  silver: { name: "Silver", price: "250,00", minAmount: 250, color: "#667085", bg: "#f7f8fa", border: "#d8dde5", description: "Naamvermelding + logo op het platform" },
  gold: { name: "Gold", price: "500,00", minAmount: 500, color: "#9a6b00", bg: "linear-gradient(135deg, #fff8dc, #fff2b3)", border: "#eed36a", description: "Naam + logo + link naar uw onderneming" },
  custom: { name: "Vrij sponsorbedrag", price: null, minAmount: null, color: "#176a2d", bg: "#EDF4E1", border: "#D7E8C8", description: "Kies zelf een sponsorbedrag als onderneming" },
};

const PRIVATE_AMOUNTS = {
  "1": { name: "€1", price: "1,00", euro: "€1", description: "Uw registratie leverde al €1 lokale hefboom op. Met deze extra bijdrage van €1 helpt u lokale bosuitbreiding in uw gemeente bijkomend te ondersteunen." },
  "2": { name: "€2", price: "2,00", euro: "€2", description: "Een extra duwtje voor meer lokaal draagvlak en meer bos." },
  custom: { name: "Vrij bedrag", price: null, description: "Hier kunt u zelf een vrij bedrag kiezen als bijdrage voor lokale bosuitbreiding." },
};

// TODO: Replace with real IBAN and beneficiary when client provides them
const IBAN = "BE77 7390 3247 0042";
const BENEFICIARY = "idBForge BV";
const LOGO_URL = "https://media.100procentlokaal.be/media/locations/brand/l/mijnstad-gemeente_nl.png";

const BosuitbreidingPage = (props) => {
  const translate = useSelector((state) => state.general.translate);
  const lang = useSelector((state) => state.general.lang);
  const { loginScreen, accountData } = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const env = getEnvEndpoints();

  // Parse URL params
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const type = params.get("type")
    || (location.pathname.includes("onderneming") ? "company" : null)
    || (location.pathname.includes("particulier") ? "private" : null)
    || "private";
  const initialPackage = params.get("package") || params.get("amount") || "1";
  const statusParam = params.get("status");

  const [selectedPackage, setSelectedPackage] = useState(initialPackage);
  const [pageState, setPageState] = useState(statusParam || "form");
  const prefillDone = useRef(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [referenceCode, setReferenceCode] = useState("");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [municipalities, setMunicipalities] = useState([]);
  const [selectedAlternativeMunicipality1, setSelectedAlternativeMunicipality1] = useState(null);
  const [selectedAlternativeMunicipality2, setSelectedAlternativeMunicipality2] = useState(null);
  const [accountId, setAccountId] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [subMunicipality, setSubMunicipality] = useState("");
  const [establishmentNumber, setEstablishmentNumber] = useState("");
  const [establishmentOptions, setEstablishmentOptions] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [sponsorPreference, setSponsorPreference] = useState("mention"); // 'mention' or 'info'

  const isCompany = type === "company";
  const config = isCompany ? COMPANY_PACKAGES[selectedPackage] : PRIVATE_AMOUNTS[selectedPackage];

  // Dropdown options
  const packageOptions = isCompany
    ? Object.entries(COMPANY_PACKAGES).map(([key, val]) => ({ value: key, label: val.name }))
    : Object.entries(PRIVATE_AMOUNTS).map(([key, val]) => ({ value: key, label: val.name }));

  const handlePackageChange = (newPackage) => {
    setSelectedPackage(newPackage);
    const newParams = new URLSearchParams(location.search);
    if (isCompany) {
      newParams.set("package", newPackage);
      newParams.delete("amount");
    } else {
      newParams.set("amount", newPackage);
      newParams.delete("package");
    }
    history.replace(`${location.pathname}?${newParams.toString()}`);
  };

  // Dynamic reference code — uses alternative municipality PC if selected, otherwise home municipality
  useEffect(() => {
    if (selectedMunicipality && accountId) {
      // Build postal code part: if 2 alts → both PCs, if 1 alt → that PC, else home PC
      let pcPart = '';
      const alt1Pc = selectedAlternativeMunicipality1?.pkancode;
      const alt2Pc = selectedAlternativeMunicipality2?.pkancode;
      if (alt1Pc && alt2Pc) {
        pcPart = `PC${alt1Pc}-PC${alt2Pc}`;
      } else if (alt1Pc) {
        pcPart = `PC${alt1Pc}`;
      } else if (alt2Pc) {
        pcPart = `PC${alt2Pc}`;
      } else {
        pcPart = `PC${selectedMunicipality.pkancode || ""}`;
      }

      if (isCompany && vatNumber) {
        const cleanVat = vatNumber.replace(/[.\s]/g, '');
        setReferenceCode(`BOSUITBREIDING - ${pcPart} - ${cleanVat}`);
      } else {
        setReferenceCode(`BOSUITBREIDING - ${pcPart} - P${accountId}`);
      }
    }
  }, [selectedMunicipality, selectedAlternativeMunicipality1, selectedAlternativeMunicipality2, accountId, isCompany, vatNumber]);

  // Check login and prefill (runs once)
  useEffect(() => {
    if (prefillDone.current) return;
    const token = localStorage.getItem("StadGemeenteToken");
    if (!token) {
      dispatch(toggleLoginScreen(true));
      setLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Token ae0e4f2df7b0d54ce458ca65ca53849e92691e86`,
    };
    axios.get(
      `${env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/municipality/?fields=id,name_nl,name_fr,name_nl,name_fr,name_de,name_en,pkancode&paginate=false&ordering=name_nl,name_fr,name_de,name_en`,
      { headers }
    ).then((res) => {
      const muniList = res.data.map((m) => ({
        value: m.id,
        label: (m.name_nl && m.name_fr && `${m.name_nl} / ${m.name_fr}`) || m[`name_${lang}`] || m.name_nl || m.name_fr || m.name_de || m.name_en,
        pkancode: m.pkancode,
      })).sort((a, b) => a.label.localeCompare(b.label));
      setMunicipalities(muniList);
    });

    axios.get(`${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/bosuitbreiding/prefill/`, {
      params: { token },
    }).then((res) => {
      const d = res.data;

      // If on generic /bosuitbreiding, redirect based on registration type
      if (location.pathname === '/bosuitbreiding') {
        const regType = d.registration_type || 'private';
        if (regType === 'company') {
          window.location.href = '/bosuitbreiding-onderneming?package=bronze';
        } else {
          window.location.href = '/bosuitbreiding-particulier?amount=1';
        }
        return;
      }

      setAccountId(d.account_id);
      setFirstName(d.first_name);
      setLastName(d.last_name);
      setEmail(d.email);
      setPhone(d.phone);
      setVatNumber(d.vat_number);
      setStreet(d.street);
      setHouseNumber(d.house_number);
      setPostalCode(d.postal_code);
      setSubMunicipality(d.sub_municipality);
      if (d.home_municipality_id) {
        setSelectedMunicipality({ value: d.home_municipality_id, label: d.home_municipality, pkancode: d.home_municipality_pkancode });
      }
      setLoading(false);
      prefillDone.current = true;

      // If VAT is prefilled and this is a company page, fetch establishments list
      if (d.vat_number && isCompany) {
        fetchEstablishments(d.vat_number);
      }
    }).catch(() => setLoading(false));
  }, [accountData]);

  const isValidBusinessNumber = (value) => {
    if (!value) return false;
    const cleaned = value.trim().replace(/[.\s]/g, '');
    return /^(BE|SN)\d{10}$/.test(cleaned);
  };

  const fillFromLookup = (d) => {
    if (d.company_name) setCompanyName(d.company_name);
    if (d.vat_number) setVatNumber(d.vat_number);
    if (d.contact_name) setContactName(d.contact_name);
    if (d.email) setEmail(d.email);
    if (d.phone) setPhone(d.phone);
    if (d.street) setStreet(d.street);
    if (d.house_number) setHouseNumber(d.house_number);
    if (d.postal_code) setPostalCode(d.postal_code);
    if (d.sub_municipality) setSubMunicipality(d.sub_municipality);
  };

  // Fetch establishments list for VAT number (does NOT fill form fields)
  const fetchEstablishments = (vatNum) => {
    const token = localStorage.getItem("StadGemeenteToken");
    axios.get(`${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/enterprise-lookup/`, {
      params: { token, lang, vat_number: vatNum },
    }).then((res) => {
      const d = res.data;
      // Only set company name from enterprise level
      if (d.company_name) setCompanyName(d.company_name);

      // Build establishment dropdown options
      if (d.establishments && d.establishments.length > 0) {
        const opts = d.establishments.map((est) => ({
          value: est.number,
          label: est.label,
          name: est.name,
        }));
        setEstablishmentOptions(opts);

        // Auto-select if only one establishment — then fetch its details
        if (d.establishments.length === 1) {
          setSelectedEstablishment(opts[0]);
          setEstablishmentNumber(opts[0].value);
          fetchEstablishmentDetails(opts[0].value, vatNum);
        }
      }
    }).catch(() => {});
  };

  // Fetch details for a specific establishment (fills form fields)
  const fetchEstablishmentDetails = (estNumber, vatNum) => {
    const token = localStorage.getItem("StadGemeenteToken");
    axios.get(`${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/enterprise-lookup/`, {
      params: { token, lang, establishment_number: estNumber, vat_number: vatNum || vatNumber },
    }).then((res) => {
      const d = res.data;
      fillFromLookup(d);
    }).catch(() => {});
  };

  // Auto-trigger when VAT reaches valid length
  const handleVatChange = (value) => {
    setVatNumber(value);
    if (isValidBusinessNumber(value)) {
      setSelectedEstablishment(null);
      setEstablishmentOptions([]);
      setEstablishmentNumber("");
      fetchEstablishments(value.trim());
    }
  };

  // When user selects an establishment from the dropdown
  const handleEstablishmentSelect = (selected) => {
    setSelectedEstablishment(selected);
    if (selected) {
      setEstablishmentNumber(selected.value);
      fetchEstablishmentDetails(selected.value);
    } else {
      setEstablishmentNumber("");
    }
  };

  const handleSubmit = (action) => {
    if (!selectedMunicipality) {
      props.createNotification("warning", translate("please_select_municipality"));
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem("StadGemeenteToken");

    axios.post(`${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/bosuitbreiding/submit/`, {
      token, contribution_type: type, package: selectedPackage,
      municipality_id: selectedMunicipality.value, action,
      alternative_municipality_1_id: selectedAlternativeMunicipality1?.value || null,
      alternative_municipality_2_id: selectedAlternativeMunicipality2?.value || null,
      custom_amount: selectedPackage === "custom" ? customAmount : null,
      company_name: companyName, vat_number: vatNumber, establishment_number: establishmentNumber, contact_name: contactName,
      email, phone, street, house_number: houseNumber, postal_code: postalCode,
      sub_municipality: subMunicipality, want_sponsor_mention: sponsorPreference === 'mention', want_more_info: sponsorPreference === 'info',
    }).then((res) => {
      setReferenceCode(res.data.reference_code);
      const newStatus = action === "payment_done" ? "payment_done" : "later";
      const newParams = new URLSearchParams(location.search);
      newParams.set("status", newStatus);
      history.replace(`${location.pathname}?${newParams.toString()}`);
      setPageState(newStatus);
      setSubmitting(false);
    }).catch(() => {
      props.createNotification("error", "Er is een fout opgetreden.");
      setSubmitting(false);
    });
  };

  if (!config) return <div className="bos-loading">Invalid URL parameters</div>;

  const token = localStorage.getItem("StadGemeenteToken");
  if (!token && !loginScreen) dispatch(toggleLoginScreen(true));

  if (loading) {
    return (
      <div id="bosuitbreidingPage">
        <Navigation {...props} />
        <div className="bos-loading">Laden...</div>
        <Footer {...props} />
      </div>
    );
  }

  // ==================== LATER STATE ====================
  if (pageState === "later") {
    return (
      <div id="bosuitbreidingPage">
        <Navigation {...props} />
        <div className="bos-container">
          <div className="bos-card">
            <div className="bos-header-bar">
              <img src={LOGO_URL} alt="mijn.stad-gemeente.be" />
              <span>{isCompany ? `${config.name} sponsoring` : "Later beslissen"}</span>
            </div>
            <div className="bos-hero bos-hero-gray">
              <h1>Geen probleem, u kunt later nog beslissen</h1>
              <p>Uw registratie blijft actief. U kunt op elk moment terugkomen om uw bijdrage of sponsoring te bevestigen.</p>
            </div>
            <div className="bos-content">
              <div className="bos-box bos-box-gray">
                <h3>Wat blijft behouden?</h3>
                <ul className="bos-list">
                  <li>Uw registratie blijft volledig actief.</li>
                  <li>Alleen de bijdrage/sponsoring is nog niet verwerkt.</li>
                </ul>
              </div>
              <div className="bos-actions">
                <button className="bos-btn-primary" onClick={() => history.push("/")}>Terug naar het platform</button>
                <button className="bos-btn-secondary" onClick={() => {
                  const newParams = new URLSearchParams(location.search);
                  newParams.delete("status");
                  history.replace(`${location.pathname}?${newParams.toString()}`);
                  setPageState("form");
                }}>
                  {isCompany ? `${config.name} sponsoring opnieuw bekijken` : "Bijdrage opnieuw bekijken"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer {...props} />
      </div>
    );
  }

  // ==================== PAYMENT DONE STATE ====================
  if (pageState === "payment_done") {
    return (
      <div id="bosuitbreidingPage">
        <Navigation {...props} />
        <div className="bos-container">
          <div className="bos-card">
            <div className="bos-header-bar">
              <img src={LOGO_URL} alt="mijn.stad-gemeente.be" />
              <span>Bevestiging ontvangen</span>
            </div>
            <div className="bos-hero bos-hero-green">
              <h1>{isCompany ? `Wij hebben uw ${config.name} sponsoring goed ontvangen` : "Wij hebben uw melding goed ontvangen"}</h1>
              <p>U ontvangt een bevestigingsmail. Na ontvangst van uw betaling volgt een tweede bevestiging.</p>
            </div>
            <div className="bos-content">
              <div className="bos-summary-card">
                <h3>{isCompany ? `Overzicht van uw ${config.name} sponsoring` : "Overzicht van uw bijdrage"}</h3>
                <div className="bos-summary-grid">
                  {isCompany ? (
                    <>
                      <div className="bos-summary-item"><div className="bos-summary-label">Bedrijfsnaam</div><div className="bos-summary-value">{companyName}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">E-mailadres</div><div className="bos-summary-value">{email}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">Gemeente</div><div className="bos-summary-value">{selectedMunicipality?.label}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">Formule</div><div className="bos-summary-value">{config.name}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">Bedrag</div><div className="bos-summary-value">{selectedPackage === "custom" ? customAmount : `€${config.price}`}</div></div>
                    </>
                  ) : (
                    <>
                      <div className="bos-summary-item"><div className="bos-summary-label">Naam</div><div className="bos-summary-value">{firstName} {lastName}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">E-mailadres</div><div className="bos-summary-value">{email}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">Thuisgemeente</div><div className="bos-summary-value">{selectedMunicipality?.label}</div></div>
                      <div className="bos-summary-item"><div className="bos-summary-label">Bedrag</div><div className="bos-summary-value">{selectedPackage === "custom" ? customAmount : `€${config.price}`}</div></div>
                    </>
                  )}
                  <div className="bos-summary-item full"><div className="bos-summary-label">Referentie</div><div className="bos-summary-value bos-reference">{referenceCode}</div></div>
                </div>
              </div>
              <div className="bos-box bos-box-gray">
                <h3>Volgende stap</h3>
                <p className="bos-helper">Na ontvangst van uw betaling met de juiste referentie wordt uw bijdrage bevestigd en ontvangt u een tweede bevestigingsmail.</p>
              </div>
              <div className="bos-actions">
                <button className="bos-btn-primary" onClick={() => history.push("/")}>Terug naar het platform</button>
                <button className="bos-btn-secondary" onClick={() => window.location.href = "mailto:support@stad-gemeente.be"}>Contact opnemen</button>
              </div>
            </div>
          </div>
        </div>
        <Footer {...props} />
      </div>
    );
  }

  // ==================== MAIN FORM STATE ====================
  return (
    <div id="bosuitbreidingPage">
      <Navigation {...props} />
      <div className="bos-container">
        <div className="bos-card">
          <div className="bos-header-bar">
            <img src={LOGO_URL} alt="mijn.stad-gemeente.be" />
            <span>{isCompany ? "Sponsoring onderneming" : "Vrijwillige bijdrage"}</span>
          </div>

          <div className="bos-hero bos-hero-green">
            <h1>
              {isCompany
                ? `Uw keuze: ${config.name} Sponsor`
                : selectedPackage === "custom"
                  ? "Uw vrijwillige bijdrage | Vrij bedrag"
                  : `Uw vrijwillige bijdrage van ${config.name}`}
            </h1>
            <p>{config.description}</p>
          </div>

          <div className="bos-content">
            <div className="bos-tier-selector">
              <label>{isCompany ? "Sponsorformule" : "Uw bijdrage"}</label>
              <div className="bos-tier-options">
                {packageOptions.map((opt) => (
                  <button
                    key={opt.value}
                    className={`bos-tier-option ${selectedPackage === opt.value ? 'active' : ''}`}
                    onClick={() => handlePackageChange(opt.value)}
                    type="button"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bos-form-grid">
              {isCompany ? (
                <>
                  <div className="bos-field"><label>Ondernemingsnummer / BTW-nummer</label><input type="text" value={vatNumber} onChange={(e) => handleVatChange(e.target.value)} placeholder="BE0123456789" /></div>
                  <div className="bos-field">
                    <label>Vestigingsnummer</label>
                    <Select
                      className="bos-establishment-select"
                      value={selectedEstablishment}
                      onChange={handleEstablishmentSelect}
                      options={establishmentOptions}
                      placeholder={translate("bosuitbreiding_select_establishment")}
                      isClearable
                      isDisabled={establishmentOptions.length === 0}
                      noOptionsMessage={() => translate("bosuitbreiding_no_establishments")}
                    />
                  </div>
                  <div className="bos-field"><label>Bedrijfsnaam</label><input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></div>
                  <div className="bos-field"><label>Contactpersoon</label><input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
                  <div className="bos-field"><label>E-mailadres</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                  <div className="bos-field"><label>Telefoonnummer</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
                  <div className="bos-field"><label>Straat</label><input type="text" value={street} onChange={(e) => setStreet(e.target.value)} /></div>
                  <div className="bos-field"><label>Huisnummer</label><input type="text" value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} /></div>
                  <div className="bos-field"><label>Postcode</label><input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></div>
                </>
              ) : (
                <>
                  <div className="bos-field"><label>Voornaam</label><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
                  <div className="bos-field"><label>Achternaam</label><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
                  <div className="bos-field"><label>E-mailadres</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                  <div className="bos-field"><label>Telefoonnummer</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
                </>
              )}

              <div className="bos-field">
                <label>{translate("bosuitbreiding_municipality_label")}</label>
                <div className="bos-readonly-municipality">
                  {selectedMunicipality ? selectedMunicipality.label : "Uw thuisgemeente"}
                </div>
              </div>

              {isCompany && (
                <div className="bos-field"><label>Deelgemeente</label><input type="text" value={subMunicipality} onChange={(e) => setSubMunicipality(e.target.value)} /></div>
              )}

              <div className="bos-field">
                <label>Bedrag</label>
                {selectedPackage === "custom" ? (
                  <input type="text" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder={isCompany ? "Bijvoorbeeld €350,00" : "Bijvoorbeeld €5,00"} />
                ) : (
                  <input type="text" value={isCompany ? `€${config.price}` : `€${config.price}`} readOnly className="bos-readonly" />
                )}
              </div>

              <div className="bos-field">
                <label>{translate("bosuitbreiding_alternative_1")}</label>
                <Select
                  className="bos-municipality-select"
                  value={selectedAlternativeMunicipality1}
                  onChange={(selected) => setSelectedAlternativeMunicipality1(selected)}
                  options={municipalities}
                  placeholder={translate("bosuitbreiding_search_alternative_municipality")}
                  isClearable
                />
              </div>

              <div className="bos-field">
                <label>{translate("bosuitbreiding_alternative_2")}</label>
                <Select
                  className="bos-municipality-select"
                  value={selectedAlternativeMunicipality2}
                  onChange={(selected) => setSelectedAlternativeMunicipality2(selected)}
                  options={municipalities}
                  placeholder={translate("bosuitbreiding_search_alternative_municipality")}
                  isClearable
                />
              </div>
            </div>

            {isCompany && (
              <div className="bos-checkboxes">
                <label><input type="radio" name="sponsorPreference" checked={sponsorPreference === 'mention'} onChange={() => setSponsorPreference('mention')} /> Wij wensen als sponsor vermeld te worden op het platform.</label>
                <label><input type="radio" name="sponsorPreference" checked={sponsorPreference === 'info'} onChange={() => setSponsorPreference('info')} /> Wij wensen graag eerst bijkomende informatie over de praktische uitwerking.</label>
              </div>
            )}

            <div className="bos-box bos-box-green">
              <h3>Voer uw overschrijving uit</h3>
              <div className="bos-payment-grid">
                <div className="bos-payment-item"><div className="bos-payment-label">Rekeningnummer</div><div className="bos-payment-value">{IBAN}</div></div>
                <div className="bos-payment-item"><div className="bos-payment-label">Begunstigde</div><div className="bos-payment-value">{
                  (() => {
                    const alt1 = selectedAlternativeMunicipality1?.label?.split(" / ")[0];
                    const alt2 = selectedAlternativeMunicipality2?.label?.split(" / ")[0];
                    const home = selectedMunicipality?.label?.split(" / ")[0];
                    if (alt1 && alt2) return `${alt1}, ${alt2}`;
                    if (alt1) return alt1;
                    if (alt2) return alt2;
                    return home || "—";
                  })()
                }</div></div>
                <div className="bos-payment-item full"><div className="bos-payment-label">Referentie / mededeling</div><div className="bos-payment-value bos-reference">{referenceCode || "Selecteer eerst een gemeente"}</div></div>
              </div>
              <p className="bos-helper">Gebruik bij uw overschrijving verplicht deze referentie, zodat wij uw bijdrage correct kunnen koppelen aan uw thuisgemeente.</p>
            </div>

            <div className="bos-box bos-box-gray">
              <h3>Na uw bevestiging</h3>
              <ul className="bos-list">
                <li>U ontvangt meteen een e-mail met uw ingevulde gegevens, bedrag en referentie.</li>
                <li>Na ontvangst van de overschrijving ontvangt u een tweede bevestiging.</li>
                {isCompany && <li>Daarna kan uw logo en sponsorvermelding worden verwerkt.</li>}
              </ul>
            </div>

            <div className="bos-actions">
              <button className="bos-btn-primary" onClick={() => handleSubmit("payment_done")} disabled={submitting}>
                {submitting ? "Bezig..." : "Ik heb deze overschrijving uitgevoerd"}
              </button>
              <button className="bos-btn-secondary" onClick={() => handleSubmit("later")} disabled={submitting}>
                Later beslissen
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer {...props} />
    </div>
  );
};

export default BosuitbreidingPage;
